import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField, Autocomplete } from "@mui/material";
import { memo } from "react";
import { NumberField } from "@base-ui/react/number-field";

import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { GeoRehandleDiggerDTO } from "../../DTOs/Geology/GeoRehandleDiggerDTO";
import { GeoRehandleTruckingTruckDTO } from "../../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { MovementLayout, ShiftWindow } from "./movementEntry";
import PlantModelSelect from "./TableCellsComponents/PlantModelSelect";
import TruckSelect from "./TableCellsComponents/TruckSelect";
import ShiftHourSelect from "./TableCellsComponents/ShiftHourSelect";
import MovementTimeSelect from "./TableCellsComponents/MovementTimeSelect";

interface InsertGeoRehandleRowProps {
  state: GeoRehandleDTO;
  setState: React.Dispatch<React.SetStateAction<GeoRehandleDTO>>;
  onAdd: () => Promise<boolean>;
  diggerOptions: GeoRehandleDiggerDTO[];
  isDiggerOptionsLoading: boolean;
  layout: MovementLayout;
  plantModels: GeoRehandleTruckingTruckDTO[];
  trucks: GeoRehandleTruckingTruckDTO[];
  isTruckOptionsLoading: boolean;
  allowOwnLoad: boolean;
  hour?: number;
  hours: string[];
  liveHourIndex: number;
  shiftWindow: ShiftWindow;
  zone: string;
  rehandleMovementFromLocationOptions: { value: number, label: string, isDumpOrCrusher: boolean }[];
  isRehandleMovementFromLocationOptionsLoading: boolean;
  rehandleMovementToLocationOptions: { value: number, label: string, isDumpOrCrusher: boolean }[];
  isRehandleMovementToLocationOptionsLoading: boolean;
  isReadOnly: boolean
  telemetryEnabled: boolean
}

export const InsertGeoRehandleRow = memo(function InsertRow({
  state,
  setState,
  onAdd,
  diggerOptions,
  isDiggerOptionsLoading,
  layout,
  plantModels,
  trucks,
  isTruckOptionsLoading,
  allowOwnLoad,
  hour,
  hours,
  liveHourIndex,
  shiftWindow,
  zone,
  rehandleMovementFromLocationOptions,
  isRehandleMovementFromLocationOptionsLoading,
  rehandleMovementToLocationOptions,
  isRehandleMovementToLocationOptionsLoading,
  isReadOnly,
  telemetryEnabled
}: InsertGeoRehandleRowProps) {
  return (
    <ul className="py-1 flex gap-2 border-b border-theme-table-border">
      {layout.timeColumn === "Hour" && <li key="hour" className={layout.time}>
        <ShiftHourSelect
          hour={hour}
          hours={hours}
          liveHourIndex={liveHourIndex}
          disabled={isReadOnly}
          onChange={value => setState(prev => ({ ...prev, hour: value }))}
        />
      </li>}
      {layout.timeColumn === "Time" && <li key="time" className={layout.time}>
        <MovementTimeSelect
          movementTimeUtc={state.movementTimeUtc}
          shiftWindow={shiftWindow}
          zone={zone}
          disabled={isReadOnly}
          onChange={movementTimeUtc => setState(prev => ({ ...prev, movementTimeUtc }))}
        />
      </li>}
      <li key="digger" className={layout.machine}>
        <Autocomplete
          classes={{ root: 'w-full' }}
          size="small"
          loading={isDiggerOptionsLoading}
          disabled={isDiggerOptionsLoading || isReadOnly}
          options={diggerOptions}
          getOptionLabel={(option) => option.plantRef}
          value={diggerOptions.find(x => x.operationId === state.operationId) ?? null}
          isOptionEqualToValue={(option, value) => option.operationId === value.operationId}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, operationId: newValue.operationId }));
            }
          }}
          renderInput={(params) => <TextField
            {...params}
            label="Digger"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isDiggerOptionsLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      {layout.carrierColumn === "Plant Model" && <li key="plant-model" className={layout.carrier}>
        <PlantModelSelect
          bucketTypeId={state.bucketTypeId}
          plantModels={plantModels}
          isLoading={isTruckOptionsLoading}
          disabled={isReadOnly}
          onChange={bucketTypeId => setState(prev => ({ ...prev, bucketTypeId }))}
          allowOwnLoad={allowOwnLoad}
          onOwnLoad={() => setState(prev => ({ ...prev, bucketTypeId: undefined }))}
        />
      </li>}
      {layout.carrierColumn === "Truck" && <li key="truck" className={layout.carrier}>
        <TruckSelect
          truckPlantId={state.truckPlantId}
          trucks={trucks}
          isLoading={isTruckOptionsLoading}
          disabled={isReadOnly}
          onChange={truck => setState(prev => ({ ...prev, truckPlantId: truck.plantId, bucketTypeId: truck.bucketTypeId }))}
          allowOwnLoad={allowOwnLoad}
          onOwnLoad={() => setState(prev => ({ ...prev, truckPlantId: undefined, bucketTypeId: undefined }))}
        />
      </li>}
      <li key="movement-from" className={layout.movement}>
        <Autocomplete
          classes={{ root: 'w-full' }}
          size="small"
          loading={isRehandleMovementFromLocationOptionsLoading}
          disabled={isRehandleMovementFromLocationOptionsLoading || isReadOnly}
          options={rehandleMovementFromLocationOptions}
          value={rehandleMovementFromLocationOptions.find(x => x.value === state.movementFromId) ?? null}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, movementFromId: newValue.value }));
            }
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <span className={`${option.isDumpOrCrusher === true ? "text-theme-danger" : ""}`}>{option.label}</span>
              </li>
            )
          }}
          renderInput={(params) => <TextField
            {...params}
            label="Movement From"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                className: `${rehandleMovementToLocationOptions.find(x => x.value === state.movementToId)?.isDumpOrCrusher === true ? "text-theme-danger" : ""}`,
                style: {
                  transition: "color 0.2s ease-in-out"
                },
                endAdornment: (
                  <>
                    {(isRehandleMovementFromLocationOptionsLoading) && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      <li key="loads" className="basis-[10%] text-[0.9rem]">
        <NumberField.Root
          disabled={isReadOnly}
          value={state.loads ?? null}
          onValueChange={(val) => {
            if (val === null || val === state.loads) return;
            setState(prev => ({ ...prev, loads: val ?? 0 }));
          }}
          className="h-full bg-theme-surface-2"
        >
          <NumberField.Group className="relative flex gap-0.5 h-full border border-theme-border rounded-sm">
            <NumberField.Decrement />
            <NumberField.Input
              placeholder="Loads"
              className={`h-full w-full px-1 py-2 bg-theme-surface-2 text-theme-text disabled:text-theme-text-muted transition`}
            />
            <NumberField.Increment />
          </NumberField.Group>
        </NumberField.Root>
      </li>
      <li key="movement-to" className={layout.movement}>
        <Autocomplete
          classes={{ root: 'w-full' }}
          size="small"
          loading={isRehandleMovementToLocationOptionsLoading}
          disabled={isRehandleMovementToLocationOptionsLoading || isReadOnly}
          options={rehandleMovementToLocationOptions}
          value={rehandleMovementToLocationOptions.find(x => x.value === state.movementToId) ?? null}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, movementToId: newValue.value }));
            }
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <span className={`${option.isDumpOrCrusher === true ? "text-theme-danger" : ""}`}>{option.label}</span>
              </li>
            )
          }}
          renderInput={(params) => {
            const selected = rehandleMovementToLocationOptions.find(x => x.value === state.movementToId);

            return <TextField
              {...params}
              label="Movement To"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  className: `${selected?.isDumpOrCrusher === true ? "text-theme-danger" : ""}`,
                  style: {
                    transition: "color 0.2s ease-in-out"
                  },
                  endAdornment: (
                    <>
                      {(isRehandleMovementToLocationOptionsLoading) && <FontAwesomeIcon icon={faSpinner} spin />}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          }}
        />
      </li>

      {telemetryEnabled && <li key="source" className="basis-[10%]" />}

      <li className="basis-[10%]">
        <button
          disabled={isReadOnly}
          type="button"
          onClick={onAdd}
          className="px-4 py-2 rounded bg-theme-info hover:bg-theme-info-hover disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed transition text-white rounded-sm border-none text-sm font-bold"
        >
          Add
        </button>
      </li>
    </ul>
  );
});